import React, { useState, useEffect, useCallback } from 'react';
import { Box, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PageHeader from 'components/Header/Page';
import FiltersBar from './FilterBar';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import LoaderComponent from 'components/Loader';
import PageWrapper from 'components/Common/PageWraper';
import QueryBuilder from './QueryBuilder';
import {
    useAdbFilters,
    ENV_OPTIONS_DEFAULT,
    commonFetchSectors,
    syncEnvWithOptions,
    commonMapRuleToApiFilter,
    buildApiSortBy,
    handleExportExcel,
    CLUSTER_DETAILS_DEFAULT_COLUMNS,
    getClusterDetailsColumns,
    QueryRule,
    FieldOption
} from 'utils/adb-common-util';
import { fetchSectorList, getWorkspaceOptions, workspaceGetEnv } from 'service/service';

export interface AdbServiceParams {
    startDate: string;
    endDate: string;
    env: string;
    sector: string;
    location: string;
    page: number;
    pageSize: number;
    apiFilters: any[];
    selectedDateInString: string;
    contentName?: string;
    exportType?: string;
    sortBy?: string;
    sortDir?: string;
}

export interface AdbFlatTableBaseProps {
    serviceFunc: (params: AdbServiceParams) => Promise<any>;
    pageTitle: string;
    breadcrumbLabel: string;
    breadcrumbPath: string;
    tableType: string;
    defaultColumnNames?: string[];
    getColumns?: () => any[];
    preserveFilterKey: string;
    initialComputeType?: string;
    showAutoTermination?: boolean;
    showClusterName?: boolean;
    buildCustomApiFilters?: (rules: QueryRule[], workspaceOptions: FieldOption[], state: { sector: string; env: string; location: string; computeType?: string }) => any[];
    // State overrides
    pageHeaderOverride?: string;
    pageSubHeaderOverride?: string;
    initialFilters?: QueryRule[];
    // Query builder options
    autoTerminationOptions?: FieldOption[];
    clusterNameOptions?: FieldOption[];
    environmentOptions?: FieldOption[];
    hideComputeType?: boolean;
    hideWorkspaceName?: boolean;
    isTopTenClusterCostJob?: boolean;
    isJob?: boolean;
}

const getNavigationFilters = (nav: any, initialComputeType: string, hideComputeType?: boolean, isTopTenClusterCostJob?: boolean, isJob?: boolean): QueryRule[] => {
    let filters: QueryRule[] = [];
    if (nav?.filters && Array.isArray(nav.filters) && nav.filters.length > 0) {
        filters = nav.filters.map((f: any) => {
            let field = f.field;
            if (['Environment', 'ComputeType', 'computetype'].includes(f.field)) {
                field = 'Compute Type';
            } else if (f.field === 'Workspace ID') {
                field = 'Workspace Name';
            }
            return { ...f, field };
        });

    } else if (nav?.computeType || nav?.workspaceName || nav?.contentName || nav?.clusterCategory) {
        let navCompute = nav.computeType || nav.clusterCategory;
        if (!navCompute || navCompute.toLowerCase() === 'all' || navCompute.toLowerCase() === 'both') {
            navCompute = initialComputeType;
        }

        // For Job reports, contentName is usually the Job Name.
        // For others, it's usually the Workspace Name.
        let filterValue: string;
        if (isTopTenClusterCostJob && nav.contentName && nav.contentName !== 'all') {
            filterValue = nav.contentName;
        } else {
            filterValue = nav.workspaceName || nav.contentName || 'all';
        }


        filters = [
            { id: '1', operator: 'AND' as const, field: 'Compute Type', value: navCompute },
            {
                id: '2',
                operator: 'AND' as const,
                field: isTopTenClusterCostJob ? 'Job Name' : 'Workspace Name',
                value: filterValue
            }
        ];
    }

    if (hideComputeType) {
        filters = filters.filter(f => f.field !== 'Compute Type');
    }
    return filters;
};

/**
 * A reusable base component for ADB flat tables (DBU, Cost, Cluster details).
 * Reduces code duplication across 7+ similar pages.
 */
const AdbFlatTableBase: React.FC<AdbFlatTableBaseProps> = ({
    serviceFunc,
    pageTitle,
    breadcrumbLabel,
    breadcrumbPath,
    tableType,
    defaultColumnNames = CLUSTER_DETAILS_DEFAULT_COLUMNS,
    getColumns = getClusterDetailsColumns,
    preserveFilterKey,
    initialComputeType = 'All',
    showClusterName = false,
    showAutoTermination = false,
    buildCustomApiFilters,
    pageHeaderOverride,
    pageSubHeaderOverride,
    initialFilters: propInitialFilters,
    autoTerminationOptions,
    clusterNameOptions,
    environmentOptions,
    hideComputeType = false,
    hideWorkspaceName = false,
    isTopTenClusterCostJob = false,
    isJob = false
}) => {
    const navigate = useNavigate();
    const {
        startDate, setStartDate, endDate, setEndDate,
        env, setEnv, sector, setSector, sectorName, setSectorName,
        location, setLocation, selectedDateInString, setSelectedDateInString,
        updateHistoryState, handleDateSelect, envRef, initialEnvAppliedRef, navigationState
    } = useAdbFilters(preserveFilterKey);

    const [loader, setLoader] = useState(false);
    const [tableRecords, setTableRecords] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
    const [sortingInfo, setSortingInfo] = useState<{ sortBy: string; sortDir: 'asc' | 'desc' | '' }>({ sortBy: '', sortDir: '' });

    const [queryBuilderFilters, setQueryBuilderFilters] = useState<QueryRule[]>(() => {
        const filters: QueryRule[] = [];
        if (!hideComputeType) {
            filters.push({ id: '1', operator: 'AND' as const, field: 'Compute Type', value: initialComputeType || 'All' });
        }
        if (!hideWorkspaceName) {
            filters.push({
                id: '2',
                operator: 'AND' as const,
                field: isTopTenClusterCostJob ? 'Job Name' : 'Workspace Name',
                value: 'all'
            });
        }
        return filters;
    });
    const [queryBuilderKey, setQueryBuilderKey] = useState(0);
    const [workspaceOptions, setWorkspaceOptions] = useState<FieldOption[]>([{ label: 'All', value: 'all' }]);
    const [envOptionsState, setEnvOptionsState] = useState<{ label: string; value: string }[]>(ENV_OPTIONS_DEFAULT);
    const [sectorList, setSectorList] = useState<string[]>([]);
    const computeType = initialComputeType;

    // Initial setup
    useEffect(() => {
        commonFetchSectors(fetchSectorList, setSectorList);
    }, []);

    useEffect(() => {
        const navFilters = getNavigationFilters(navigationState, initialComputeType, hideComputeType, isTopTenClusterCostJob, isJob);
        const validPropFilters = !!(propInitialFilters && propInitialFilters.length > 0);

        // Navigation (drilldown) should take precedence over default prop filters
        let filters: QueryRule[] = [];
        if (navFilters.length > 0) {
            filters = navFilters;
        } else if (validPropFilters) {
            filters = [...(propInitialFilters || [])];
        }

        if (hideComputeType) {
            filters = filters.filter(f => f.field !== 'Compute Type');
        }
        if (hideWorkspaceName) {
            filters = filters.filter(f => f.field !== 'Workspace Name' && f.field !== 'Workspace ID');
        }

        if (filters.length === 0) return;


        // Recovery logic: ensure essential filters are present if they were missing from navFilters or propInitialFilters
        const hasComputeFilter = filters.some(f => ['Compute Type', 'Environment', 'Status', 'computetype', 'ComputeType'].includes(f.field));
        const hasWorkspaceFilter = filters.some(f => f.field === 'Workspace Name' || f.field === 'Workspace ID');
        const hasJobFilter = filters.some(f => f.field === 'Job Name');

        if (!hideWorkspaceName && !hasWorkspaceFilter && !isTopTenClusterCostJob) {
            filters.push({ id: 'ws-recovery', operator: 'AND' as const, field: 'Workspace Name', value: 'all' });
        }
        if (!hideComputeType && !hasComputeFilter) {
            filters.push({ id: 'ct-recovery', operator: 'AND' as const, field: 'Compute Type', value: initialComputeType });
        }
        if (isTopTenClusterCostJob && !hasJobFilter) {
            filters.push({ id: 'job-recovery', operator: 'AND' as const, field: 'Job Name', value: 'all' });
        }

        setQueryBuilderFilters(filters);
        setQueryBuilderKey(prev => prev + 1);
    }, [navigationState, initialComputeType, propInitialFilters]);

    useEffect(() => {
        const getEnv = async (loc: string) => {
            try {
                const res = await workspaceGetEnv(loc);
                const options = [{ label: 'ALL', value: 'both' }, ...(res?.data || [])];
                setEnvOptionsState(options);
                syncEnvWithOptions(options, initialEnvAppliedRef, envRef, navigationState?.env, setEnv);
            } catch (err) { console.error(err); }
        };
        getEnv(location);
    }, [location]);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const resp = await getWorkspaceOptions(startDate, endDate, env, sector, location, selectedDateInString);
                if (resp?.success && resp?.data) {
                    let options: FieldOption[] = [{ label: 'All', value: 'all' }];
                    if (Array.isArray(resp.data.workspaceName)) {
                        options.push(...resp.data.workspaceName.map((n: string) => ({ label: n, value: n, workspaceId: n })));
                    } else if (Array.isArray(resp.data)) {
                        options.push(...resp.data.map((w: any) => ({ label: w.workspace_name, value: w.workspace_id, workspaceId: w.workspace_id })));
                    }
                    setWorkspaceOptions(options);
                }
            } catch (e) { console.error(e); }
        };
        fetchOptions();
    }, [startDate, endDate, env, sector, location, selectedDateInString]);

    const buildApiFilters = useCallback(() => {
        if (buildCustomApiFilters) {
            return buildCustomApiFilters(queryBuilderFilters, workspaceOptions, { sector, env, location, computeType });
        }

        const apiFilters: any[] = [
            { operator: 'OR', field: 'Sector', value: (sector && sector !== 'all') ? sector : 'All' },
            { operator: 'OR', field: 'Environment', value: (env && env !== 'both') ? env : 'All' },
            { operator: 'AND', field: 'Location', value: (location && location !== 'ALL') ? location : 'All' }
        ];

        queryBuilderFilters.forEach(rule => {
            const mapped = commonMapRuleToApiFilter(rule, workspaceOptions);
            if (mapped) apiFilters.push(mapped);
        });

        if (!apiFilters.some(f => f.field === 'Compute Type')) {
            apiFilters.push({ operator: 'OR', field: 'Compute Type', value: computeType });
        }
        if (!apiFilters.some(f => f.field === 'Workspace ID')) {
            apiFilters.push({ operator: 'AND', field: 'Workspace ID', value: 'All' });
        }

        return apiFilters;
    }, [queryBuilderFilters, workspaceOptions, sector, env, location, computeType, buildCustomApiFilters]);

    const fetchData = useCallback(async (isExport = false) => {
        if (!isExport) setLoader(true);
        try {
            const apiFilters = buildApiFilters();
            const apiSortBy = buildApiSortBy(sortingInfo, getColumns());

            const resp = await serviceFunc({
                startDate,
                endDate,
                env,
                sector,
                location,
                page: isExport ? 1 : page,
                pageSize: isExport ? 10000 : pageSize,
                apiFilters,
                selectedDateInString,
                exportType: isExport ? 'excel' : undefined,
                sortBy: apiSortBy,
                sortDir: sortingInfo.sortDir || undefined
            });

            if (isExport) {
                handleExportExcel(resp);
            } else if (resp?.success && resp.data?.data) {
                setTableRecords(resp.data.data);
                const totalElements = resp.data.pagination?.total || resp.data.data.length;
                setPaginationData({
                    totalElements,
                    totalPages: resp.data.pagination?.totalPages || Math.ceil(totalElements / pageSize)
                });
            } else {
                setTableRecords([]);
                setPaginationData({ totalElements: 0, totalPages: 0 });
            }
        } catch (e) {
            console.error(e);
            if (!isExport) {
                setTableRecords([]);
                setPaginationData({ totalElements: 0, totalPages: 0 });
            }
        } finally { if (!isExport) setLoader(false); }
    }, [startDate, endDate, env, sector, location, selectedDateInString, page, pageSize, sortingInfo, buildApiFilters, serviceFunc, getColumns]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleRunQuery = (rules: QueryRule[]) => {
        setQueryBuilderFilters(rules);
        setPage(1);
    };

    const pageSubHeader = pageSubHeaderOverride ?? navigationState?.pageSubHeader ?? pageTitle;
    const contentName = navigationState?.contentName ?? '';
    const dateLabel = navigationState?.dateLabel;

    const renderTable = () => {
        if (loader) return <LoaderComponent pt={20} />;
        if (tableRecords.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 10, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0', mx: 2 }}>
                    <Box sx={{ fontSize: '1.2rem', color: '#666', mb: 1 }}>No Data Available</Box>
                    <Box sx={{ fontSize: '0.9rem', color: '#999' }}>No records found for selected filters.</Box>
                </Box>
            );
        }
        return (
            <Box sx={{ px: 2 }}>
                <CustomizedDataGrid
                    title={`${pageTitle} (${paginationData.totalElements.toLocaleString()} records)`}
                    tableColumns={getColumns()} tableData={tableRecords} tableType={tableType}
                    defaultColumnNames={defaultColumnNames} page={page} pageSize={pageSize}
                    paginationData={paginationData} listLength={tableRecords.length}
                    handlepageSizeChange={(e: any) => { setPageSize(Number(e.target.value)); setPage(1); }}
                    handlePageChange={(_: any, v: number) => setPage(v)}
                    isDateRangeFilter={false} isDisabled={false} isFetching={loader}
                    showExport={true} exportTableData={() => fetchData(true)}
                    onSorting={(info: any) => setSortingInfo(info?.sortBy && info.sortDir ? info : { sortBy: '', sortDir: '' })}
                    currSorting={sortingInfo}
                />
            </Box>
        );
    };

    return (
        <PageWrapper>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2, px: 2 }}>
                <Box sx={{ mb: 2, fontSize: '1.125rem', fontWeight: '600', color: '#003771' }}>
                    <span style={{ cursor: 'pointer' }} onClick={() => navigate(navigationState?.breadcrumbPath || breadcrumbPath, { state: { startDate, endDate, env, sector, location, selectedDateInString } })}>
                        {navigationState?.pageHeader || breadcrumbLabel}
                    </span>
                    {' > '}
                    <span style={{ cursor: 'pointer' }} onClick={() => navigate(-1)}>
                        {pageSubHeader}{contentName ? ` (${contentName})` : ''}
                    </span>
                    {' > '}
                    <span>
                        {dateLabel || 'Details'}
                        {((selectedDateInString === 'customDateRange' && startDate && endDate && startDate !== endDate)) &&
                            ` (${startDate} to ${endDate})`
                        }
                    </span>
                </Box>
                <Box sx={{ zIndex: 1 }}>
                    <PageHeader title=" " icon={''} lastSync={null}>
                        <FiltersBar
                            location={location} setLocation={setLocation}
                            env={env} setEnv={setEnv} envOptions={envOptionsState} setEnvOptions={setEnvOptionsState}
                            startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate}
                            handleDateSelect={handleDateSelect} selectedDateInString={selectedDateInString} setSelectedDateInString={setSelectedDateInString}
                            sectorName={sectorName}
                            setSectorName={(v: any) => {
                                const resolved = typeof v === 'function' ? v(sectorName) : v;
                                setSectorName(resolved); setSector(resolved);
                            }}
                            updateHistoryState={updateHistoryState} sectorOptionsList={sectorList}
                        />
                    </PageHeader>
                </Box>
            </Stack>

            <Box sx={{ px: 2, mb: 2 }}>
                <QueryBuilder
                    key={queryBuilderKey}
                    title="Filter Options"
                    sectorOptions={workspaceOptions}
                    environmentOptions={environmentOptions || [{ label: 'All', value: 'All' }, { label: 'INTERACTIVE', value: 'INTERACTIVE' }, { label: 'JOB', value: 'JOB' }, { label: 'SQL', value: 'SQL' }]}
                    locationOptions={[]}
                    teamFlagOptions={[]}
                    onRunQuery={handleRunQuery}
                    showQueryPreference={false}
                    initialFilters={queryBuilderFilters}
                    showClusterName={showClusterName}
                    showAutoTermination={showAutoTermination}
                    autoTerminationOptions={autoTerminationOptions}
                    clusterNameOptions={clusterNameOptions}
                    hideComputeType={hideComputeType}
                    hideWorkspaceName={hideWorkspaceName}
                    isTopTenClusterCostJob={isTopTenClusterCostJob}
                    isJob={isJob}
                />
            </Box>

            {renderTable()}
        </PageWrapper>
    );
};

export default AdbFlatTableBase;
